// import React from "react";
// import Skeleton from "@mui/material/Skeleton";
// import Card from "@mui/material/Card";
// import CardContent from "@mui/material/CardContent";
// import { Typography } from "@mui/material";

// export default function MetricCard({
//   title,
//   value,
//   icon: Icon,
//   color = "primary",
//   isLoading,
// }) {
//   const colorClasses = {
//     primary: {
//       bg: "bg-blue-50",
//       iconBg: "bg-blue-100",
//       iconText: "text-blue-600",
//     },
//     success: {
//       bg: "bg-green-50",
//       iconBg: "bg-green-100",
//       iconText: "text-green-600",
//     },
//     warning: {
//       bg: "bg-amber-50",
//       iconBg: "bg-amber-100",
//       iconText: "text-amber-600",
//     },
//   };

//   const colors = colorClasses[color] || colorClasses["primary"];

//   if (isLoading) {
//     return (
//       <Card>
//         <div className="px-4 py-2">
//           <div className="flex justify-between pb-2">
//             <Skeleton className="h-5 w-2/3" />
//             <Skeleton className="h-8 w-8 rounded-full" />
//           </div>
//           <CardContent className="pt-0">
//             <Skeleton className="h-8 w-1/2" />
//           </CardContent>
//         </div>
//       </Card>
//     );
//   }

//   return (
//     <Card elevation={0} className={`${colors.bg} shadow-sm`}>
//       <div className="px-4 py-2">
//         <div className="flex justify-between pb-2">
//           <Typography className="text-sm font-medium text-gray-700">
//             {title}
//           </Typography>
//           <div className={`p-2 rounded-lg ${colors.iconBg}`}>
//             <Icon className={`h-5 w-5 ${colors.iconText}`} />
//           </div>
//         </div>
//         <CardContent className="pt-0">
//           <div className="text-3xl font-bold text-gray-900">{value}</div>
//         </CardContent>
//       </div>
//     </Card>
//   );
// }

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme,
  alpha,
  keyframes,
} from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Keyframes for animations
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const countUpAnimation = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled component for the card with animation
export const AnimatedCard = styled(Card)(({ theme, color }) => {
  const colors = {
    primary: {
      light: theme.palette.primary.light,
      main: theme.palette.primary.main,
      dark: theme.palette.primary.dark,
    },
    success: {
      light: "#d1e7dd",
      main: "#198754",
      dark: "#0f5132",
    },
    warning: {
      light: "#fff3cd",
      main: "#ffc107",
      dark: "#664d03",
    },
    error: {
      light: "#fee2e2",
      main: "#ef4444",
      dark: "#991b1b",
    },
  };

  const selected = colors[color] || colors.primary;

  return {
    background: `linear-gradient(135deg, ${alpha(
      selected.light,
      0.3
    )} 0%, ${alpha(selected.light, 0.1)} 100%)`,
    borderLeft: `4px solid ${selected.main}`,
    borderRadius: "16px",
    boxShadow: `0 4px 20px 0 ${alpha(selected.main, 0.1)}`,
    transition: "all 0.3s ease",
    animation: `${floatAnimation} 6s ease-in-out infinite`,
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: `0 12px 28px 0 ${alpha(selected.main, 0.2)}`,
      animation: "none",
    },
  };
});

// Styled icon wrapper with animation
export const IconWrapper = styled(Box)(({ theme, color }) => {
  const colors = {
    primary: theme.palette.primary,
    success: {
      light: "#d1e7dd",
      main: "#198754",
      dark: "#0f5132",
    },
    warning: {
      light: "#fff3cd",
      main: "#ffc107",
      dark: "#664d03",
    },
    error: {
      light: "#fee2e2",
      main: "#ef4444",
      dark: "#991b1b",
    },
  };

  const selected = colors[color] || colors.primary;

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    padding: theme.spacing(1.5),
    backgroundColor: alpha(selected.main, 0.1),
    color: selected.main,
    boxShadow: `0 2px 8px 0 ${alpha(selected.main, 0.2)}`,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "rotate(10deg) scale(1.1)",
      backgroundColor: alpha(selected.main, 0.2),
    },
  };
});

// Styled value with animation
const AnimatedValue = styled(Typography)(({ theme }) => ({
  animation: `${countUpAnimation} 1s ease-out forwards`,
  fontWeight: 700,
  fontSize: "3.5rem",
  lineHeight: 1.1,
  marginBottom: theme.spacing(1),
}));

export default function MetricCard({
  title,
  value,
  icon: Icon,
  color = "primary",
  isLoading,
  trend,
  description,
  onClick,
  isActive,
}) {
  const theme = useTheme();
  const [displayValue, setDisplayValue] = useState(0);

  // Count-up animation for values
  useEffect(() => {
    if (!isLoading && value !== undefined) {
      let current = 0;
      const increment = Math.ceil(value / 50);
      const updateValue = () => {
        current += increment;
        if (current >= value) {
          setDisplayValue(value);
        } else {
          setDisplayValue(current);
          setTimeout(updateValue, 30);
        }
      };
      updateValue();
    }
  }, [value, isLoading]);

  const colorValues = {
    primary: theme.palette.primary,
    success: {
      light: "#d1e7dd",
      main: "#198754",
      dark: "#0f5132",
    },
    warning: {
      light: "#fff3cd",
      main: "#ffc107",
      dark: "#664d03",
    },
    error: {
      light: "#fee2e2",
      main: "#ef4444",
      dark: "#991b1b",
    },
  };

  const selectedColor = colorValues[color] || colorValues.primary;

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          animation: `${pulseAnimation} 2s infinite`,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
          <Skeleton variant="text" width="40%" height={72} />
        </Box>
      </Card>
    );
  }

  return (
    <AnimatedCard
      color={color}
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        ...(isActive && {
          outline: `3px solid ${selectedColor.main}`,
          outlineOffset: "2px",
          boxShadow: `0 0 0 4px ${selectedColor.main}22`,
        }),
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
          <IconWrapper color={color}>
            <Icon sx={{ fontSize: 24 }} />
          </IconWrapper>
        </Box>
        <CardContent sx={{ p: 0 }}>
          <AnimatedValue
            sx={{
              color: selectedColor.dark,
            }}
          >
            {displayValue.toLocaleString()}
          </AnimatedValue>
          {trend && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 1,
              }}
            >
              {trend.direction === "up" ? (
                <TrendingUp
                  sx={{
                    fontSize: 18,
                    color: theme.palette.success.main,
                    mr: 0.5,
                  }}
                />
              ) : (
                <TrendingDown
                  sx={{
                    fontSize: 18,
                    color: theme.palette.error.main,
                    mr: 0.5,
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color:
                    trend.direction === "up"
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  fontWeight: 500,
                }}
              >
                {trend.value}%{" "}
                {trend.direction === "up" ? "increase" : "decrease"}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, ml: 1 }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Box>
    </AnimatedCard>
  );
}
